import { shallowRef, toRaw, unref, computed, reactive, watch, watchEffect, Ref } from "vue";
import Attribute, { AttributeType } from "./Attribute";
import type AttributeMeta from "../xmlrdb-metadata/Attribute";
import type XMLRDBElement from "../xmlrdb-metadata/Element";
import ElementArray from "./ElementArray";
import DataElement from "./DataElement";

export default class ReferenceAttribute<T extends AttributeType> extends Attribute<T> {

  foreignKeyAttributes: AttributeMeta[];
  lookupElement: XMLRDBElement;
  lookupIdAttributes: AttributeMeta[];
  idmapping: { fkattr: AttributeMeta; lookup: AttributeMeta }[];
  inverseIdMap: Map<AttributeMeta, AttributeMeta>;
  _selected: Ref<Record<string,any>| null>;

  selected = computed({
    set: (val) => {
      this._selected.value = val;
      this.setForeignkeyValues(this._selected.value);
    },
    get: () => {
      return this.getSelected();
    }
  })

  LOOKUPS = computed(() => this.getLookups());
  label = computed<string>(() => {
    return this.lookupElement.getLookupLabel(this.selected.value);
  });

  constructor(initialValue, options) {
    super(initialValue, options);

    this.foreignKeyAttributes = this.metadata.element().getForeignKeyAttributes(this.metadata);

    this.lookupElement = this.metadata.lookupClass();
    this.lookupIdAttributes = this.lookupElement.getPrimaryKeyAttributes();
    // IDMapping besteht aus der Liste der (potentiell mehrwrtigen) Foreign-Keys
    // jedes Objekt:
    //   fkattr: Attribut im DataElement (Metadaten)
    //   lookup: Attibute in der Lookupklasse
    this.idmapping = [];
    this.inverseIdMap = new Map(); // Mapping von Lookup Id -> FK-Id um die Lookup-Id von der fkid aus zu ermitteln
    this.foreignKeyAttributes.forEach(attribute => {
      const lookup = this.lookupIdAttributes[attribute.fkPosition()];
      if(!lookup )
        console.error(`Lookup attribute not found for foreign key attribute ${attribute.name()}`);
      this.idmapping.push({
        fkattr: attribute,
        lookup: lookup
      });
      this.inverseIdMap.set(lookup, attribute);
    });
    this._selected = shallowRef();
  }

  referencedDataElement = computed(() => this.findReferencedDataElement());

  getSelected(){
    const currentId = this.getCurrentId();
    if (currentId.filter(a => a === null).length > 0) {
      // console.log(this.metadata.name(), "idIsNull")
      return null;
    }
    let selected = this.searchLookupAttributes(currentId);
    if (selected) {
      // console.log(this.metadata.name(),"idFound")
      return selected;
    }

    try {
      for (let i = 0; i < this.idmapping.length; i++) {
        let idpair = this.idmapping[i];
        if (
          this.dataElement.$[idpair.fkattr.name()] !==
          this._selected.value?.[idpair.lookup.name()]
        ) {
          // id Missmatch zwischen _selected und den fk-Feldern vom DataElement bedeutet, dass noch kein element über die GUI ausgewählt wurde, dass in _selected geschrieben wurde oder dass nach dem Laden die Referenz noch nicht geladen wurde. Kommt selten vor, ist nur zur sicherheit hier.
          console.log(this.metadata.name(), "idMissmatch");
          return this.createDummy();
        }
      }
    } catch (error) {
      console.error("Error getting selected value:", error);
    }
    // console.log(this.metadata.name(),"selected");
    return this._selected.value;
  }

  createDummy() {
    let attrs = this.lookupElement.newAttributes();
    try {
      for (const idpair of this.idmapping) {
        attrs[idpair.lookup.name()] = this.dataElement.$[idpair.fkattr.name()]
      }
    } catch (error) {
      console.error("Error creating dummy attributes:", error);
    }
    return attrs;
  }

  compareValueFunc(): string {
    return this.label.value;
  }

  getCurrentId() {
    let currentId = [] as Array<AttributeType>;
    this.foreignKeyAttributes.forEach(attribute => {
      currentId[attribute.fkPosition()] = this.dataElement.$[attribute.name()]
    });
    return currentId;
  }

  getLookupElementArray() {
    let root = this.dataElement.parentByLevel(this.metadata.lookupClassLevel());
    return root.subElements[this.lookupElement.id()] as ElementArray;
  }

  getLookups() {
    return this.getLookupElementArray().dataElements.map(function ( dataElement: DataElement) {
      let copy = {} as Record<string,any>;
      for (let key in dataElement.$) {
        copy[key] = dataElement.$[key];
      }
      return copy;
    });
  }

  /*
    Sucht in dem Lookup-Array nach dem passenden Element
  */
  searchLookupAttributes(idValues) {
    let lookupData = this.LOOKUPS.value.find(lookup => {
      for (let i = 0; i < this.lookupIdAttributes.length; i++) {
        let idName = this.lookupIdAttributes[i].name();
        if (idValues[i] !== lookup[idName]) {
          return false;
        }
      }
      return true;
    });

    let val = null;
    if (lookupData)
      val = lookupData;
    return val;
  }

  setForeignkeyValues(val) {
    try {
      for (let i = 0; i < this.idmapping.length; i++) {
        let idpair = this.idmapping[i];
        if (val)
          this.dataElement.$[idpair.fkattr.name()] = val[idpair.lookup.name()];
        else {
          this.dataElement.$[idpair.fkattr.name()] = null;
        }
      }
    } catch (error) {
      console.error("Error setting foreign key values:", error);
    }
  }
  
  findReferencedDataElement() {
    try {
    const id = this.lookupIdAttributes.map((attr) => {
      const fkattr = this.inverseIdMap.get(attr)
      if(fkattr === undefined) {
        throw new Error(`Foreign key attribute not found for: ${attr.name()}`);
      }
      return this.dataElement.$[fkattr.name()];
    }).join(";");
    return this.getLookupElementArray().byId[id];
  
  } catch (error) {
    console.error("Error finding referenced data element:", error);
    return null;
  }
  }
}